/**
 * Fechas del dominio como cadenas 'YYYY-MM-DD'.
 *
 * Todo el motor trabaja con dias civiles, no con instantes. Usar `Date` para
 * representarlos arrastra la zona horaria del navegador y hace que un registro
 * de las 00:30 caiga en el dia anterior segun donde este el movil.
 */

export type IsoDate = string;

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseIsoDate(value: IsoDate): { year: number; month: number; day: number } {
  const m = ISO_RE.exec(value);
  if (!m) throw new Error(`Fecha no válida (se esperaba YYYY-MM-DD): ${value}`);
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

/** ¿Es un día del calendario? '1990-13-45' tiene la forma pero no existe. */
export function isValidIsoDate(value: IsoDate): boolean {
  return ISO_RE.test(value) && fromDayNumber(toDayNumber(value)) === value;
}

/** Dias transcurridos desde 1970-01-01, para aritmetica sin zonas horarias. */
export function toDayNumber(value: IsoDate): number {
  const { year, month, day } = parseIsoDate(value);
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

export function fromDayNumber(days: number): IsoDate {
  return new Date(days * 86_400_000).toISOString().slice(0, 10);
}

export function addDays(value: IsoDate, days: number): IsoDate {
  return fromDayNumber(toDayNumber(value) + days);
}

/** Positivo si `b` es posterior a `a`. */
export function daysBetween(a: IsoDate, b: IsoDate): number {
  return toDayNumber(b) - toDayNumber(a);
}

/** Lista de dias [desde, hasta], ambos incluidos. */
export function dateRange(from: IsoDate, to: IsoDate): IsoDate[] {
  const start = toDayNumber(from);
  const end = toDayNumber(to);
  if (end < start) return [];
  const out: IsoDate[] = [];
  for (let d = start; d <= end; d++) out.push(fromDayNumber(d));
  return out;
}

/** Los `count` dias que terminan en `end`, del mas antiguo al mas reciente. */
export function lastNDays(end: IsoDate, count: number): IsoDate[] {
  if (count <= 0) return [];
  return dateRange(addDays(end, -(count - 1)), end);
}
